<script lang="ts">
  import { untrack } from 'svelte';
  import Icon, { type IconName } from './Icon.svelte';
  import { groupByModule, matchParts, type PartListItem } from '../lib/partItem';
  import { t } from '../i18n/index.svelte';

  let {
    items,
    selectedId,
    hiddenIds,
    search = $bindable(''),
    exact = false,
    onSelect,
    onToggle,
    onToggleVisible,
    onToggleModule,
    groupSel = new Set<string>(),
    explodeActive = false,
    onToggleGroupSel,
    onSelectAllGroups,
    onExplodeSel,
    onHideSel,
    onIsolateSel,
    onClearSel,
    labelsActive = false,
    onToggleLabels,
    onScan,
    onZoomSelected,
    onShowAll,
    canZoom = false,
    zoomedIn = false,
    hasHidden = false,
  }: {
    items: PartListItem[];
    selectedId: number | null;
    hiddenIds: Set<number>;
    search?: string;
    /** true khi từ khóa đến từ QUÉT QR → lọc DUY NHẤT theo mã (code), bỏ tìm-text tên/cụm. */
    exact?: boolean;
    onSelect: (id: number) => void;
    onToggle: (id: number) => void;
    onToggleVisible: (id: number) => void;
    /** ẩn/hiện cả cụm — nhận danh sách id các chi tiết trong cụm đang hiển thị. */
    onToggleModule?: (ids: number[]) => void;
    /** Sơ đồ tách rời theo cụm: tập tên cụm đang chọn + các hành động trên tập chọn. */
    groupSel?: Set<string>;
    explodeActive?: boolean; // đang tách rời cụm đã chọn → nút Explode ở trạng thái bật
    onToggleGroupSel?: (module: string) => void;
    /** Chọn TẤT CẢ cụm (truyền danh sách tên cụm) hoặc bỏ chọn hết (null). */
    onSelectAllGroups?: (modules: string[] | null) => void;
    onExplodeSel?: () => void;
    onHideSel?: () => void;
    onIsolateSel?: () => void;
    onClearSel?: () => void;
    labelsActive?: boolean; // đang hiện nhãn sơ đồ → nút Nhãn bật
    onToggleLabels?: () => void;
    onScan?: () => void;
    /** Zoom vào chi tiết đang chọn. Chỉ truyền ở bản DESKTOP (mobile đặt nút ở tay cầm sheet → tránh trùng). */
    onZoomSelected?: () => void;
    /** Hiện lại toàn bộ chi tiết đang ẩn. Chỉ truyền ở bản desktop (xem onZoomSelected). */
    onShowAll?: () => void;
    canZoom?: boolean; // có chi tiết đang chọn → cho phép zoom
    zoomedIn?: boolean; // đã zoom vào chi tiết → nút đổi thành "Về tổng thể"
    hasHidden?: boolean; // đang ẩn ≥ 1 chi tiết → hiện nút "Hiện lại toàn bộ"
  } = $props();

  // luật khớp nằm ở lib/partItem (hàm thuần → test được); component chỉ nối rune vào.
  const filtered = $derived(matchParts(items, search, exact));
  const groups = $derived(groupByModule(filtered));

  // Số chi tiết trong lựa chọn hiện tại: cụm → tổng chi tiết các cụm; else chi tiết đơn (click) = 1.
  const selCount = $derived(
    groupSel.size > 0
      ? items.filter((it) => groupSel.has(it.module)).length
      : selectedId != null
        ? 1
        : 0,
  );

  // thu gọn/mở rộng từng cụm (lưu tên module ĐANG MỞ trong component) — mặc định đóng toàn bộ
  let expanded = $state(new Set<string>());
  function toggleCollapse(m: string) {
    const next = new Set(expanded);
    if (next.has(m)) next.delete(m);
    else next.add(m);
    expanded = next;
  }
  // Chỉ mời "Thu gọn tất cả" khi MỌI cụm đang mở; còn lại (kể cả init đóng hết, hay 1 cụm tự mở theo
  // chi tiết được chọn) → mời "Mở rộng tất cả". Khớp mong đợi: khởi tạo đóng → nút "Mở rộng tất cả".
  const allExpanded = $derived(groups.length > 0 && groups.every((g) => expanded.has(g.module)));
  function collapseAll() {
    expanded = new Set();
  }
  function expandAll() {
    expanded = new Set(groups.map((g) => g.module));
  }

  // đang tìm / quét QR → mở các cụm có kết quả (kẻo kết quả nằm trong cụm đóng)
  $effect(() => {
    if (search.trim()) expanded = new Set(groups.map((g) => g.module));
  });
  // chọn chi tiết (trên 3D, QR…) → mở cụm chứa nó; untrack để user vẫn đóng được cụm đó
  $effect(() => {
    const id = selectedId;
    const g = groups.find((gr) => gr.items.some((it) => it.id === id));
    if (!g) return;
    untrack(() => {
      if (!expanded.has(g.module)) expanded = new Set(expanded).add(g.module);
    });
  });

  function categoryIcon(cat: string): IconName {
    if (cat === 'door') return 'hingeL';
    if (cat === 'hardware') return 'cube';
    return 'edge';
  }
</script>

<div class="part-list">
  <div class="search">
    <Icon name="search" size={16} />
    <input type="text" placeholder={t('list.search')} bind:value={search} />
    {#if onScan}
      <button class="scan" title={t('list.scan')} aria-label={t('list.scan')} onclick={onScan}>
        <Icon name="scan" size={18} />
      </button>
    {/if}
  </div>

  {#if onSelectAllGroups || groups.length > 1}
    {@const allGroupsSel = groups.length > 0 && groups.every((g) => groupSel.has(g.module))}
    <div class="list-tools">
      <!-- Chọn TẤT CẢ cụm. "Zoom" + "Hiện lại toàn bộ" đã chuyển vào thanh hành động (sel-bar) bên dưới. -->
      {#if onSelectAllGroups}
        <button
          class="grp-sel"
          class:on={allGroupsSel}
          role="checkbox"
          aria-checked={allGroupsSel}
          title={t('list.selectAll')}
          aria-label={t('list.selectAll')}
          onclick={() => onSelectAllGroups(allGroupsSel ? null : groups.map((g) => g.module))}
        >
          {#if allGroupsSel}<Icon name="check" size={12} />{/if}
        </button>
      {/if}
      {#if groups.length > 1}
        <button
          class="collapse-all"
          title={allExpanded ? t('list.collapseAll') : t('list.expandAll')}
          onclick={() => (allExpanded ? collapseAll() : expandAll())}
        >
          <Icon name={allExpanded ? 'collapse' : 'expand'} size={14} />
          <span>{allExpanded ? t('list.collapseAll') : t('list.expandAll')}</span>
        </button>
      {/if}
    </div>
  {/if}

  <!-- Thanh hành động trên lựa chọn (chi tiết đơn HOẶC cụm): hiện khi đang chọn thứ gì đó. -->
  {#if selCount > 0 || (hasHidden && onShowAll)}
    <div class="sel-bar">
      {#if groupSel.size > 0}
        <span class="sel-count">{t('sel.count', { n: groupSel.size })}</span>
      {:else if selectedId != null}
        <span class="sel-count">{t('sel.part')}</span>
      {:else}
        <span class="sel-count">{t('sel.hidden')}</span>
      {/if}
      <div class="sel-acts">
        {#if onZoomSelected && selCount > 0}
          <button
            class="sel-act"
            class:active={zoomedIn}
            title={zoomedIn ? t('common.overview') : t('common.zoomSelected')}
            aria-label={zoomedIn ? t('common.overview') : t('common.zoomSelected')}
            disabled={!zoomedIn && !canZoom}
            onclick={onZoomSelected}
          >
            <Icon name={zoomedIn ? 'reset' : 'fit'} size={16} />
          </button>
        {/if}
        {#if onExplodeSel && selCount > 0}
          <!-- Explode chỉ bật khi lựa chọn có ≥2 chi tiết. -->
          <button class="sel-act" class:active={explodeActive} title={t('sel.explode')} aria-label={t('sel.explode')} disabled={selCount < 2} onclick={onExplodeSel}>
            <Icon name="explode" size={16} />
          </button>
        {/if}
        {#if onHideSel && selCount > 0}
          <button class="sel-act" title={t('sel.hide')} aria-label={t('sel.hide')} onclick={onHideSel}>
            <Icon name="eyeOff" size={16} />
          </button>
        {/if}
        {#if onIsolateSel && selCount > 0}
          <button class="sel-act" title={t('sel.hideOthers')} aria-label={t('sel.hideOthers')} onclick={onIsolateSel}>
            <Icon name="isolate" size={16} />
          </button>
        {/if}
        {#if onToggleLabels && selCount > 0}
          <!-- Nhãn sơ đồ lắp đặt (GĐ2): tên chi tiết + đường dẫn. -->
          <button class="sel-act" class:active={labelsActive} title={t('sel.labels')} aria-label={t('sel.labels')} onclick={onToggleLabels}>
            <Icon name="label" size={16} />
          </button>
        {/if}
        <!-- Hiện lại toàn bộ chi tiết đang ẩn — chuyển từ hàng công cụ trên vào đây; ẩn khi đang tách (explode tự quản ẩn). -->
        {#if onShowAll && hasHidden && !explodeActive}
          <button class="sel-act" title={t('list.showAll')} aria-label={t('list.showAll')} onclick={onShowAll}>
            <Icon name="eye" size={16} />
          </button>
        {/if}
        {#if onClearSel && selCount > 0}
          <button class="sel-act clear" title={t('sel.clear')} aria-label={t('sel.clear')} onclick={onClearSel}>
            <Icon name="close" size={15} />
          </button>
        {/if}
      </div>
    </div>
  {/if}

  <div class="scroll">
    {#each groups as g (g.module)}
      {@const groupHidden = g.items.every((it) => hiddenIds.has(it.id))}
      {@const isCollapsed = !expanded.has(g.module)}
      {@const grpSelected = groupSel.has(g.module)}
      <div class="module-head" class:collapsed={isCollapsed} class:grp-on={grpSelected}>
        {#if onToggleGroupSel}
          <button
            class="grp-sel"
            class:on={grpSelected}
            role="checkbox"
            aria-checked={grpSelected}
            title={t('list.selectGroup')}
            aria-label={t('list.selectGroup')}
            onclick={() => onToggleGroupSel(g.module)}
          >
            {#if grpSelected}<Icon name="check" size={12} />{/if}
          </button>
        {/if}
        <button
          class="module-toggle"
          aria-expanded={!isCollapsed}
          title={isCollapsed ? t('list.expand') : t('list.collapse')}
          onclick={() => toggleCollapse(g.module)}
        >
          <span class="chev"><Icon name="chevronDown" size={14} /></span>
          <span class="module-label">{g.module || t('list.moduleOther')}</span>
          <span class="count">{g.items.length}</span>
        </button>
        {#if onToggleModule}
          <button
            class="vis module-vis"
            class:on={groupHidden}
            title={groupHidden ? t('list.showGroup') : t('list.hideGroup')}
            aria-label={groupHidden ? t('list.showGroup') : t('list.hideGroup')}
            onclick={() => onToggleModule(g.items.map((it) => it.id))}
          >
            <Icon name={groupHidden ? 'eyeOff' : 'eye'} size={16} />
          </button>
        {/if}
      </div>
      {#if !isCollapsed}
        {#each g.items as it (it.id)}
        {@const shownCode = it.designation ?? it.code}
        <div class="row" class:selected={it.id === selectedId} class:hidden={hiddenIds.has(it.id)}>
          <button class="main" onclick={() => onSelect(it.id)}>
            <span class="ico"><Icon name={categoryIcon(it.category)} size={18} /></span>
            <span class="text">
              <span class="name">{it.name}</span>
              <span class="sub">{shownCode ? `${t('list.codePrefix', { code: shownCode })} · ` : ''}{it.dims}</span>
            </span>
          </button>
          {#if it.animatable}
            <button
              class="toggle"
              title={t('list.toggleMotion')}
              aria-label={t('list.toggleMotion')}
              onclick={() => onToggle(it.id)}
            >
              <Icon name="slide" size={16} />
            </button>
          {/if}
          <button
            class="vis"
            title={hiddenIds.has(it.id) ? t('list.show') : t('list.hide')}
            aria-label={hiddenIds.has(it.id) ? t('list.show') : t('list.hide')}
            onclick={() => onToggleVisible(it.id)}
          >
            <Icon name={hiddenIds.has(it.id) ? 'eyeOff' : 'eye'} size={16} />
          </button>
        </div>
        {/each}
      {/if}
    {/each}

    {#if filtered.length === 0}
      <p class="empty">{t('list.empty')}</p>
    {/if}
  </div>
</div>

<style>
  .part-list {
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: 100%;
  }
  .search {
    display: flex;
    align-items: center;
    gap: var(--space-xs);
    padding: var(--space-xs) var(--space-sm);
    margin-bottom: var(--space-xs);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-control);
    color: var(--color-text-secondary);
  }
  .search input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font: inherit;
    color: var(--color-text-primary);
  }
  .search .scan {
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    margin: -4px -4px -4px 0;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: var(--color-primary);
    cursor: pointer;
  }
  .search .scan:hover {
    background: var(--color-canvas-bottom);
  }
  .list-tools {
    display: flex;
    align-items: center;
    gap: var(--space-xs);
    margin-bottom: 2px;
  }
  .collapse-all {
    margin-left: auto; /* luôn đẩy "Thu gọn tất cả" sang phải, dù có/không có ô chọn-tất-cả */
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 8px;
    border: none;
    background: transparent;
    color: var(--color-text-secondary);
    font: inherit;
    font-size: var(--font-size-caption);
    font-weight: 600;
    cursor: pointer;
    border-radius: var(--radius-control);
  }
  .collapse-all:hover {
    color: var(--color-primary);
    background: var(--color-canvas-bottom);
  }
  .scroll {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
    margin: 0 calc(-1 * var(--space-xs));
    padding: 0 var(--space-xs);
  }
  .module-head {
    display: flex;
    align-items: center;
    gap: var(--space-xs);
    margin: var(--space-sm) 0 4px;
  }
  .module-head.grp-on {
    background: color-mix(in srgb, var(--color-primary) 10%, transparent);
    border-radius: var(--radius-control);
  }
  /* ô chọn cụm (sơ đồ tách rời) */
  .grp-sel {
    flex: none;
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1.5px solid var(--color-border);
    border-radius: 5px;
    background: var(--color-surface);
    color: #fff;
    cursor: pointer;
  }
  .grp-sel:hover {
    border-color: var(--color-primary);
  }
  .grp-sel.on {
    background: var(--color-primary);
    border-color: var(--color-primary);
  }
  /* thanh hành động trên tập cụm đã chọn */
  .sel-bar {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: var(--space-xs) 0;
    padding: var(--space-sm);
    background: color-mix(in srgb, var(--color-primary) 8%, var(--color-surface));
    border: 1px solid color-mix(in srgb, var(--color-primary) 30%, var(--color-border));
    border-radius: var(--radius-control);
  }
  .sel-count {
    font-size: var(--font-size-caption);
    font-weight: 700;
    color: var(--color-text-primary);
  }
  .sel-acts {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  /* nút chỉ-icon (text thành tooltip) để tiết kiệm chỗ */
  .sel-act {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 34px;
    padding: 0;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-control);
    background: var(--color-surface);
    color: var(--color-text-primary);
    cursor: pointer;
  }
  .sel-act:hover:not(:disabled) {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }
  .sel-act:disabled {
    opacity: 0.4;
    cursor: default;
  }
  .sel-act.active {
    background: var(--color-primary);
    border-color: var(--color-primary);
    color: #fff;
  }
  .sel-act.clear {
    margin-left: auto;
    color: var(--color-text-secondary);
  }
  .sel-act.clear:hover {
    border-color: var(--color-error);
    color: var(--color-error);
  }
  .module-toggle {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 2px 4px;
    border: none;
    background: transparent;
    cursor: pointer;
    text-align: left;
    border-radius: var(--radius-control);
  }
  .module-toggle:hover {
    background: var(--color-canvas-bottom);
  }
  .chev {
    flex: none;
    display: inline-flex;
    color: var(--color-text-secondary);
    transition: transform var(--motion-base, 0.15s) ease;
  }
  .module-head.collapsed .chev {
    transform: rotate(-90deg); /* ▼ (mở) → ▶ (thu gọn) */
  }
  .module-label {
    flex: 1;
    min-width: 0;
    font-size: var(--font-size-caption);
    font-weight: 700;
    color: var(--color-text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .count {
    flex: none;
    font-size: var(--font-size-caption);
    font-weight: 600;
    color: var(--color-text-secondary);
    opacity: 0.7;
  }
  .module-vis {
    flex: none;
    width: 30px;
    height: 26px;
    border: none;
    background: transparent;
    color: var(--color-text-secondary);
    cursor: pointer;
    border-radius: var(--radius-control);
  }
  .module-vis:hover {
    color: var(--color-primary);
    background: var(--color-surface);
  }
  .module-vis.on {
    color: var(--color-primary);
  }
  .row {
    display: flex;
    align-items: stretch;
    border: 1px solid transparent;
    border-radius: var(--radius-control);
    margin-bottom: 4px;
  }
  .row:hover {
    background: var(--color-canvas-bottom);
  }
  .row.selected {
    border-color: var(--color-part-highlight);
    background: var(--color-canvas-top);
  }
  .main {
    flex: 1;
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    padding: var(--space-xs) var(--space-sm);
    border: none;
    background: transparent;
    font: inherit;
    text-align: left;
    cursor: pointer;
    min-width: 0;
  }
  .ico {
    color: var(--color-primary);
    display: inline-flex;
  }
  .text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .name {
    font-weight: 600;
    color: var(--color-text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .sub {
    font-size: var(--font-size-caption);
    color: var(--color-text-secondary);
  }
  .toggle {
    flex: none;
    width: 40px;
    border: none;
    background: transparent;
    color: var(--color-text-secondary);
    cursor: pointer;
    border-radius: var(--radius-control);
  }
  .toggle:hover {
    color: var(--color-primary);
    background: var(--color-surface);
  }
  .vis {
    flex: none;
    width: 38px;
    border: none;
    background: transparent;
    color: var(--color-text-secondary);
    cursor: pointer;
    border-radius: var(--radius-control);
  }
  .vis:hover {
    color: var(--color-primary);
    background: var(--color-surface);
  }
  .row.hidden .main {
    opacity: 0.45;
  }
  .row.hidden .vis {
    color: var(--color-primary);
  }
  .empty {
    color: var(--color-text-secondary);
    font-size: var(--font-size-caption);
    text-align: center;
    padding: var(--space-lg) 0;
  }
</style>
